/**
 * Where a publicly shared view lives, if anywhere.
 *
 * Every one of the seven view types can be shared -- `can_share` is true for
 * all of them on the backend, and every view carries a slug from the moment it
 * is created. What differs is whether this build has a page to send anybody to,
 * and that is what this map answers. Exhaustive over `ViewTypeName`, so an
 * eighth view type has to say where its public page is before it compiles.
 *
 * Sharing is offered only where the answer is not null: a link nobody can open
 * is not a link.
 */
import type { View, ViewTypeName } from '@/api/types'
import { webFrontendUrl } from '@/shared/config/runtime'

const PUBLIC_PATHS = {
  form: '/form',
  grid: '/public/grid',
  // Five types with no public page. They are not unshareable -- the backend
  // would share any of them today, and the application draws all five -- there
  // is just nowhere to send somebody without an account.
  gallery: null,
  board: null,
  schedule: null,
  gantt: null,
  list: null,
} satisfies Record<ViewTypeName, string | null>

/** Whether this build could send somebody to this view. */
export function canShareView(view: View): boolean {
  return PUBLIC_PATHS[view.type] !== null
}

/**
 * The link to hand out, which exists whether or not the view is shared.
 *
 * The slug is not created by sharing: it is on the view from the moment the
 * view is, so turning the switch on can show the address at once rather than
 * waiting for a round trip to learn something the client already knew.
 */
export function publicViewUrl(view: View): string | null {
  const path: string | null = PUBLIC_PATHS[view.type]
  if (path === null) return null
  return `${webFrontendUrl()}${path}/${view.slug}`
}
